import React from 'react';
import { Event } from '../../services/events.service';
import { getInitials } from '../../utils/helpers';
import './EventsList.css';

type Props = {
  events: Event[];
  canEdit: boolean;
  onCreate: () => void;
  onView: (event: Event) => void;
  onEdit: (event: Event) => void;
  onDelete: (id: string) => void;
};

const formatEventDate = (event: Event) => {
  const date = event.eventDate ? new Date(event.eventDate) : new Date(event.createdAt);
  return {
    day: date.getDate(),
    month: date.toLocaleDateString('en-US', { month: 'short' }),
    year: date.getFullYear(),
    time: event.eventTime || null,
  };
};

const EventsList: React.FC<Props> = ({ events, canEdit, onCreate, onView, onEdit, onDelete }) => {
  return (
    <div className="upcoming-events-card sidebar-card">
      <div className="ue-header">
        <span className="ue-header-icon">🏆</span>
        <h4 className="ue-title">Upcoming Events</h4>
        {canEdit && (
          <button className="ue-add-btn" onClick={onCreate} aria-label="Add event" type="button">
            +
          </button>
        )}
      </div>
      <div className="ue-list">
        {events.length === 0 ? (
          <div className="ue-empty">
            <span className="ue-empty-icon">📅</span>
            <p className="ue-empty-text">No upcoming events</p>
            {canEdit && (
              <button type="button" className="ue-empty-cta" onClick={onCreate}>
                Add your first event
              </button>
            )}
          </div>
        ) : (
          events.map((event) => {
            const { day, month, year, time } = formatEventDate(event);
            return (
              <div
                key={event.id}
                className="ue-event-card"
                onClick={() => onView(event)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onView(event);
                  }
                }}
              >
                <div className="ue-card-date-badge">
                  <span className="ue-date-day">{day}</span>
                  <span className="ue-date-month">{month}</span>
                  {year !== new Date().getFullYear() && <span className="ue-date-year">{year}</span>}
                </div>
                <div className="ue-card-content">
                  <h3 className="ue-card-title">{event.title}</h3>
                  {event.description && (
                    <p className="ue-card-desc">{event.description}</p>
                  )}
                  <div className="ue-card-meta">
                    {time && (
                      <span className="ue-card-time">🕐 {time}</span>
                    )}
                    <span className="ue-card-author">
                      {event.createdBy?.fullName ? getInitials(event.createdBy.fullName) : '—'} · {event.createdBy?.fullName || 'Admin'}
                    </span>
                  </div>
                </div>
                {canEdit && (
                  <div className="ue-card-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="ue-card-action edit"
                      onClick={() => onEdit(event)}
                      title="Edit event"
                      aria-label="Edit event"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="ue-card-action delete"
                      onClick={() => onDelete(event.id)}
                      title="Delete event"
                      aria-label="Delete event"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default EventsList;
